import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, CreditCard, Loader2 } from "lucide-react";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { CarregandoBloco, CarregandoFormulario } from "@/components/carregando";

import { ROTA_FORMAS } from "../rotas";
import ResumoForma from "./ResumoForma";
import SecaoIdentificacao from "./SecaoIdentificacao";
import SecaoIntegracoes from "./SecaoIntegracoes";
import SecaoModulos from "./SecaoModulos";
import SecaoTaxa from "./SecaoTaxa";
import useEditarForma from "./useEditarForma";

function Rodape({ d, rotulo, aoCancelar, aoSalvar }) {
  return (
    <div className="shrink-0 border-t bg-card px-4 sm:px-6 py-3">
      <div className="max-w-[1400px] mx-auto flex items-center justify-end gap-3">
        <button type="button" onClick={aoCancelar}
          className="h-10 px-4 rounded-lg border border-red-500/40 bg-red-500/10 text-red-500 text-sm font-medium hover:bg-red-500/20 transition">Cancelar</button>
        <button type="button" onClick={aoSalvar} disabled={d.salvando || d.carregando} data-e2e="salvar-forma"
          className="h-12 px-7 rounded-lg bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50">
          {d.salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}{d.salvando ? "Salvando…" : rotulo}
        </button>
      </div>
    </div>
  );
}

function titulos(criando, canal) {
  if (canal) return [`${canal.nome}: taxa e recebimento`, canal.descricao];
  if (criando) return ["Nova forma de pagamento", "Nome, taxa, prazo de recebimento e onde ela aparece."];
  return ["Editar forma de pagamento", "Nome, taxa, prazo de recebimento e onde ela aparece."];
}

// Financeiro → Formas de Pagamento → editar/nova em tela inteira (v1.5.784;
// antes era um popup). Mesmo molde da Nova Despesa: resumo à direita, rodapé preso.
export default function EditarFormaPage() {
  const { id, chave } = useParams();
  const navigate = useNavigate();
  const d = useEditarForma({ id, chaveCanal: chave });
  const [sair, setSair] = useState(false);
  const criando = !id && !chave;
  const canal = d.info.canal;
  const [titulo, subtitulo] = titulos(criando, canal);

  const voltar = () => navigate(ROTA_FORMAS);
  const cancelar = () => (d.alterado ? setSair(true) : voltar());
  const salvar = async () => { if (await d.salvar()) voltar(); };

  return (
    <div className="h-full flex flex-col overflow-hidden" data-e2e="editar-forma">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 py-4 sm:py-5 space-y-4">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={cancelar} aria-label="Voltar para Formas de Pagamento"
              className="h-10 w-10 rounded-lg border flex items-center justify-center hover:bg-muted shrink-0"><ArrowLeft className="w-4 h-4" /></button>
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><CreditCard className="w-5 h-5 text-primary" /></div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold leading-tight">{titulo}</h1>
              <p className="text-sm text-muted-foreground">{subtitulo}</p>
            </div>
          </div>
          {/* Carregamento com cards (25/10/2026): o formulário e o resumo piscando no lugar do spinner */}
          {d.carregando ? (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px] items-start">
              <CarregandoFormulario campos={chave ? 3 : 6} />
              <CarregandoBloco altura="h-72" />
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px] items-start">
              <div className="space-y-4 min-w-0">
                {!chave && <SecaoIdentificacao d={d} criando={criando} />}
                <SecaoTaxa d={d} nomeTipo={canal?.nome} />
                {!chave && <SecaoModulos d={d} />}
                {!chave && <SecaoIntegracoes d={d} />}
              </div>
              <ResumoForma form={d.form} canal={canal} />
            </div>
          )}
        </div>
      </div>
      <Rodape d={d} rotulo={criando ? "Criar forma de pagamento" : "Salvar alterações"} aoCancelar={cancelar} aoSalvar={salvar} />

      <AlertDialog open={sair} onOpenChange={setSair}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar as alterações?</AlertDialogTitle>
            <AlertDialogDescription>O que você mudou nesta tela será perdido.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={voltar}>Descartar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
